import React from 'react'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useCompensationPopupStyles } from '../styles'
import PayoutLabel from '../components/PayoutLabel'
import { Compensation, CompensationResponse } from '../../../../../api/salaryCalculation'
import { usePayoutPopupContext } from '../context'
import PayoutSummary from '../components/PayoutSummary'
import DisplayFormatedNumber from '../../../../../components/DisplayFormatedNumber'
import { getFormattedDate } from '../../../utils/getDateScales'

type CompensationKey = keyof Pick<
  CompensationResponse,
  'sicknessCompensation' | 'vacationCompensation' | 'publicHolidaysCompensation' | 'pregnancyCompensation'
>

const fieldToLabelMap: Record<CompensationKey, string> = {
  sicknessCompensation: 'sickness_compensation_hours',
  vacationCompensation: 'vacation_compensation_hours',
  publicHolidaysCompensation: 'public_holidays_compensation_hours',
  pregnancyCompensation: 'pregnancy_compensation_hours',
}

const getCompensationPayoutFormula = (data: CompensationResponse, compensation: Compensation | null) => {
  const totalRevenue = <DisplayFormatedNumber value={data.totalRevenuePreviousMonths.totalRevenue} format="euroInt" />
  const totalHours = `${data.totalContractedHoursPreviousMonths.totalHours} h`
  const absenceCompensation = <DisplayFormatedNumber value={compensation?.absenceCompensation ?? 0} format="euroInt" />

  return (
    <>
      ({totalRevenue} / {totalHours}) x {`${compensation?.hours} h`} = {absenceCompensation}
    </>
  )
}

const CompensationContent = ({ contentType }: { contentType: CompensationKey }) => {
  const classes = useStyles()
  const commonClasses = useCompensationPopupStyles()
  const { data } = usePayoutPopupContext()
  const { t } = useTranslation()

  if (!data) {
    return null
  }

  const bonusRateLabelClassName = {
    label: clsx(commonClasses.labelAsAccordion, classes.bonusRateLabel),
    value: clsx(commonClasses.labelAsAccordion, classes.bonusRateLabel),
  }

  return (
    <div>
      <PayoutLabel
        classes={{ label: commonClasses.labelAsAccordion, value: commonClasses.labelAsAccordion }}
        label={`1. ${t('compensation_popup_total_revenue_last_3_month')}:`}
        value={data.totalRevenuePreviousMonths.totalRevenue}
        format="euroInt"
      />
      <ul className={classes.bonusRateList}>
        {data.totalRevenuePreviousMonths.months.map((monthEntry) => (
          <li key={monthEntry.month}>
            <PayoutLabel
              classes={bonusRateLabelClassName}
              label={getFormattedDate(monthEntry.month)}
              value={monthEntry.total}
              format="euroInt"
            />
          </li>
        ))}
      </ul>
      <PayoutLabel
        classes={{ label: commonClasses.labelAsAccordion, value: commonClasses.labelAsAccordion }}
        label={`2. ${t('compensation_popup_total_contracted_hours_last_3_month')}:`}
        value={`${data.totalContractedHoursPreviousMonths.totalHours} hours`}
        format="text"
      />
      <ul className={classes.bonusRateList}>
        {data.totalContractedHoursPreviousMonths.months.map((monthEntry) => (
          <li key={monthEntry.month}>
            <PayoutLabel
              classes={bonusRateLabelClassName}
              label={getFormattedDate(monthEntry.month)}
              value={`${monthEntry.contractedWeeklyWorkHours} h`}
              format="text"
            />
          </li>
        ))}
      </ul>

      <PayoutLabel
        classes={{ label: commonClasses.labelAsAccordion, value: commonClasses.labelAsAccordion }}
        label={`3. ${t(fieldToLabelMap[contentType])}:`}
        value={`${data[contentType]?.hours} h`}
        format="text"
      />

      <PayoutSummary
        className={classes.summaryWrapper}
        label={t(`compensation_popup_${fieldToLabelMap[contentType]}_label`)}
      >
        {getCompensationPayoutFormula(data, data[contentType])}
      </PayoutSummary>
    </div>
  )
}

export default CompensationContent

const useStyles = makeStyles({
  bonusRateList: {
    margin: 0,
  },
  bonusRateLabel: {
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: 13,
    fontWeight: 500,
  },
  summaryWrapper: {
    marginRight: 12,
  },
})
